'use client';

/**
 * `useCountInBeats`, the count-in as data rather than as a curtain.
 *
 * The Studio's own count-in draws itself: `absolute inset-0 z-20 bg-black/30`
 * plus a radial wash, a full-screen flash on every beat, and a 241 × 264 px
 * glass card at the exact centre of the roll. In the journey that lasts a
 * median **3.3 s for 5.4 s of music** — half the cycle on the repertoire this
 * product targets — and it covers the one thing the player has to prepare:
 * what is coming.
 *
 * It also swallowed the keyboard: the overlay listens for `Space`/`Enter` on
 * `window` and skips, so the very key that starts a pass cancelled its own
 * count-in on the second press, and any click anywhere did the same.
 *
 * So the journey does not render it. The audio scheduling is untouched — the
 * clicks are still `CountInComponent`'s, sample-accurate on the audio clock —
 * and this hook exposes the beats so the panel can count them in the room it
 * already has.
 */

import { useEffect, useState } from 'react';
import { useStudio } from '@/contexts/studio-context';

export type CountInBeats = {
  /** 0-based beat currently sounding. */
  readonly beat: number;
  readonly total: number;
};

export function useCountInBeats(): CountInBeats | null {
  const { studio } = useStudio();
  const [beats, setBeats] = useState<CountInBeats | null>(null);

  useEffect(() => {
    if (studio === null) {
      return;
    }

    return studio.countIn.on((event) => {
      if (event.type === 'start' || event.type === 'beat') {
        setBeats({ beat: event.beat, total: event.totalBeats });

        return;
      }

      setBeats(null);
    });
  }, [studio]);

  return beats;
}
